import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Theme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "goproprojects.theme";
const DEFAULT_THEME: Theme = "light";

function getInitialTheme(): Theme {
  if (typeof window === "undefined") return DEFAULT_THEME;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (stored === "dark" || stored === "light") return stored;
  } catch {
    // localStorage can throw in private mode or restricted contexts.
  }
  return DEFAULT_THEME;
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

export function ThemeProvider({
  children,
  storageKey = STORAGE_KEY,
  defaultTheme = DEFAULT_THEME,
}: {
  children: ReactNode;
  storageKey?: string;
  defaultTheme?: Theme;
}) {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);

  useEffect(() => {
    setThemeState(getInitialTheme());
  }, []);

  useEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage.setItem(storageKey, theme);
    } catch {
      // Ignore storage errors.
    }
  }, [theme, storageKey]);

  const setTheme = (next: Theme) => setThemeState(next);
  const toggleTheme = () => setThemeState((t) => (t === "dark" ? "light" : "dark"));

  return <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

export function createThemeInitScript({
  storageKey = STORAGE_KEY,
  defaultTheme = DEFAULT_THEME,
}: {
  storageKey?: string;
  defaultTheme?: Theme;
}): string {
  return `
    (function () {
      try {
        var stored = window.localStorage.getItem(${JSON.stringify(storageKey)});
        var theme = stored === "dark" || stored === "light" ? stored : ${JSON.stringify(defaultTheme)};
        if (theme === "dark") document.documentElement.classList.add("dark");
      } catch (e) {
        // Ignore storage errors.
      }
    })();
  `.trim();
}
